import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Label,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import MenuPermissionContainer from "./menupermissioncontainer";
import MenuPermissionForm from "./menupermissionform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchMenuPermissionsThunk,
  addMenuPermissionThunk,
  deleteMenuPermissionThunk,
  fetchMenusThunk,
  fetchPermissionsThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionEnum } from "../../../Components/constants/permissions";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  menuPermissions: rbac.menuPermissions,
  menus: rbac.menus,
  permissions: rbac.permissions,
  loading: rbac.loading,
}));

const MenuPermissionMasterPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { menuPermissions, menus, permissions, loading } = useSelector(rbacSelector);
  const userPermissions = usePermissions();

  const menuPermissionList = menuPermissions || [];
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
  const [selectedPermissionId, setSelectedPermissionId] = useState<number | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMenuPermissionId, setSelectedMenuPermissionId] = useState<number | null>(null);

  const loadMenuPermissionList = useCallback(() => {
    return dispatch(fetchMenuPermissionsThunk({
      menuid: selectedMenuId || 0,
      permissionid: selectedPermissionId || 0,
      status: 0,
    }));
  }, [dispatch, selectedMenuId, selectedPermissionId]);

  useEffect(() => {
    // Fetch menus and permissions on mount
    dispatch(fetchMenusThunk({
      parentid: 0,
      moduleid: 0,
      menuid: 0,
      status: 0,
    }));
    dispatch(fetchPermissionsThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchMenuPermissionsThunk({
      menuid: selectedMenuId || 0,
      permissionid: selectedPermissionId || 0,
      status: 0,
    }));
  }, [dispatch, selectedMenuId, selectedPermissionId]);

  // Filter to show only active menus
  const activeMenus = React.useMemo(() => {
    if (!Array.isArray(menus)) return [];
    return menus.filter((menu: any) => 
      menu.status?.toLowerCase() === "active"
    );
  }, [menus]);

  const menuOptions = activeMenus.map((menu) => ({
    label: menu.title || menu.menuname,
    value: menu.menuid,
  }));

  // Filter to show only active permissions
  const activePermissions = React.useMemo(() => {
    if (!Array.isArray(permissions)) return [];
    return permissions.filter((permission: any) => 
      permission.status?.toLowerCase() === "active"
    );
  }, [permissions]);

  const permissionOptions = activePermissions.map((permission) => ({
    label: permission.permissionname,
    value: permission.permissionid,
  }));

  const handleMenuChange = (selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      setSelectedMenuId(selectedOption.value);
    } else {
      setSelectedMenuId(null);
    }
  };

  const handlePermissionChange = (selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      setSelectedPermissionId(selectedOption.value);
    } else {
      setSelectedPermissionId(null);
    }
  };

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedMenuPermissionId(id);
    setDeleteModalOpen(true);
  };

  // Helper function to check for duplicate menu-permission combination
  const checkDuplicateMenuPermission = useCallback((menuid: number, permissionid: number): boolean => {
    if (!menuid || !permissionid) return false;
    
    const exists = menuPermissionList.some(
      (menuPermission: any) =>
        Number(menuPermission.menuid) === Number(menuid) &&
        Number(menuPermission.permissionid) === Number(permissionid)
    );
    
    return exists;
  }, [menuPermissionList]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        menuid: Yup.number().required("Menu is required"),
        permissionid: Yup.number().required("Permission is required"),
      }),
    []
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      menuid: "",
      permissionid: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const menuId = Number(values.menuid) || 0;
      const permissionId = Number(values.permissionid) || 0;

      // Additional validation check before submission (safety measure)
      if (checkDuplicateMenuPermission(menuId, permissionId)) {
        toast.error("This menu-permission combination already exists. Please select a different combination.");
        return;
      }

      // Close modal immediately
      setIsModalOpen(false);
      resetForm();

      try {
        const res = await dispatch(
          addMenuPermissionThunk({
            menuid: menuId,
            permissionid: permissionId,
          })
        );
        if (!res?.success) {
          toast.error(res?.message || "Failed to add menu permission");
          return;
        }
        toast.success("Menu permission added successfully!");
        await dispatch(fetchMenuPermissionsThunk({
          menuid: selectedMenuId || 0,
          permissionid: selectedPermissionId || 0,
          status: 0,
        }));
      } catch (err) {
        console.error("Error saving menu permission:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm({
        values: {
          menuid: "",
          permissionid: "",
        },
      });
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const columns = useMemo(
    () => [
      {
        header: "Menu Permission ID",
        accessorKey: "menupermissionid",
        cell: ({ row }: any) => {
          const id = row.original.menupermissionid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Application Name", accessorKey: "applicationname" },
      { header: "Module Name", accessorKey: "modulename" },
      { header: "Menu Name", accessorKey: "menuname" },
      { header: "Permission Name", accessorKey: "permissionname" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      ...(userPermissions.includes(PermissionEnum.UPDATE_MENUPERMISSION) || userPermissions.includes(PermissionEnum.DELETE_MENUPERMISSION) ? [{
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <div className="d-flex justify-content-center align-items-center">
              {userPermissions.includes(PermissionEnum.DELETE_MENUPERMISSION) && (
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.menupermissionid);  
                  }}
                  title="Delete"
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              )}
            </div>
          );
        },
      }] : []),
    ],
    [handleDeleteClick, userPermissions]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Menu Permission Master" pageTitle={["RBAC", "Menu Permission Master"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={menuOptions}
                placeholder="Select Menu"
                value={menuOptions.find((opt) => opt.value === selectedMenuId) || null}
                onChange={handleMenuChange}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={permissionOptions}
                placeholder="Select Permission"
                value={permissionOptions.find((opt) => opt.value === selectedPermissionId) || null}
                onChange={handlePermissionChange}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          <Col md={4} className="text-end d-flex align-items-center justify-content-end">
            {userPermissions.includes(PermissionEnum.ADD_MENUPERMISSION) && (
              <Button color="primary" onClick={toggleModal}>
                + Add New
              </Button>
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  menuPermissionList.length > 0 ? (
                    <MenuPermissionContainer
                      columns={columns}
                      data={menuPermissionList}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add New Menu Permission
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <MenuPermissionForm 
              validation={validation}
              menus={menus}
              permissions={permissions}
              menuPermissions={menuPermissionList}
            />
          </ModalBody>
          <ModalFooter>
            {userPermissions.includes(PermissionEnum.ADD_MENUPERMISSION) && (
              <Button
                type="submit"
                color="primary"
              >
                Add
              </Button>
            )}
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Menu Permission"
        message="Are you sure you want to delete this Menu Permission?"
        onConfirm={async () => {
          if (selectedMenuPermissionId) {
            setDeleteModalOpen(false);
            const menupermissionid = selectedMenuPermissionId;
            setSelectedMenuPermissionId(null);
            try {
              const res = await dispatch(
                deleteMenuPermissionThunk(menupermissionid)
              );
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete menu permission");
                return;
              }
              toast.success("Menu permission deleted successfully!");
              await dispatch(fetchMenuPermissionsThunk({
                menuid: selectedMenuId || 0,
                permissionid: selectedPermissionId || 0,
                status: 0,
              }));
            } catch (err) {
              console.error("Error deleting menu permission:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default MenuPermissionMasterPage;
